import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { EmptyState, PetSelect, getPreferredPetId } from "@/components/shared";
import { HelpTip, PAGE_HELP } from "@/components/help";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import {
  BondScoreCard, CareCircleCard, CoachPromptCard, EnrichmentCard, MoodSnapshotCard,
  QuickCheckinCard, RecentLogsCard, ReportsReadyCard, RitualCard, StreakChip, WellnessSnapshotCard,
} from "@/components/dashboard-cards";
import { PawPrint } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [petId, setPetId] = useState(null);
  const [score, setScore] = useState(null);
  const [rituals, setRituals] = useState([]);
  const [wellness, setWellness] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [careCircle, setCareCircle] = useState([]);
  const [reports, setReports] = useState([]);
  const [enrichment, setEnrichment] = useState(null); // today's enrichment ideas record
  const [loading, setLoading] = useState(true);
  const [calcBusy, setCalcBusy] = useState(false);
  const [coachPrompt, setCoachPrompt] = useState("");
  const [ci, setCi] = useState({ humanMood: "", petMood: "", stressLevel: 5, connectionQuality: 7, notes: "" });
  const [ciBusy, setCiBusy] = useState(false);
  const [streak, setStreak] = useState(null);
  const [regenBusy, setRegenBusy] = useState(false);

  const regeneratePlan = async () => {
    setRegenBusy(true);
    try {
      const { data } = await api.post("/ai/daily-plan/regenerate", { petId });
      if (data.ritual) setRituals((prev) => [data.ritual, ...prev.filter((r) => r.dailyFor !== data.ritual.dailyFor)]);
      if (data.enrichment) setEnrichment(data.enrichment);
      toast.success(`Fresh plan ready${data.ritual ? `: "${data.ritual.title}"` : ""} — completely new, nothing repeated.`);
    } catch (e) { toast.error(errMsg(e)); } finally { setRegenBusy(false); }
  };

  const pet = useMemo(() => pets.find((p) => p.id === petId), [pets, petId]);

  const fetchStreak = async () => {
    try { const { data } = await api.get("/daily-checkins/streak"); setStreak(data); return data; }
    catch (e) { console.warn("Streak fetch failed:", e?.message); return null; }
  };
  useEffect(() => { fetchStreak(); }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/pet-profiles");
        setPets(data);
        const pref = getPreferredPetId(data);
        if (pref) setPetId(pref);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (!petId) return;
    const load = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [s, r, w, c, cc, rp, en] = await Promise.all([
          api.get(`/bond-scores?petId=${petId}&limit=1`),
          api.get(`/rituals?petId=${petId}&status=active&limit=3`),
          api.get(`/wellness-logs?petId=${petId}&limit=5`),
          api.get(`/daily-checkins?petId=${petId}&limit=5`),
          api.get(`/care-circle-members?petId=${petId}&limit=5`),
          api.get(`/reports?petId=${petId}&limit=3`),
          api.get(`/enrichment-ideas?petId=${petId}&date=${today}&limit=1`),
        ]);
        setScore(s.data[0] || null);
        setRituals(r.data);
        setWellness(w.data);
        setCheckins(c.data);
        setCareCircle(cc.data);
        setReports(rp.data);
        setEnrichment(en.data[0] || null);
      } catch (e) { toast.error(errMsg(e)); }
    };
    load();
  }, [petId]);

  const calcScore = async () => {
    setCalcBusy(true);
    try {
      const { data } = await api.post("/bond-scores/calculate", { petId });
      setScore(data);
      toast.success(`Bond Score updated: ${data.score}/100`);
    } catch (e) { toast.error(errMsg(e)); } finally { setCalcBusy(false); }
  };

  const refreshDailyPlan = async (pid) => {
    // The daily ritual + enrichment ideas are generated server-side after a check-in;
    // poll for up to ~30s so they appear without a manual refresh.
    const today = new Date().toISOString().slice(0, 10);
    for (let attempt = 0; attempt < 6; attempt++) {
      await new Promise((res) => setTimeout(res, 5000));
      try {
        const [en, r] = await Promise.all([
          api.get(`/enrichment-ideas?petId=${pid}&date=${today}&limit=1`),
          api.get(`/rituals?petId=${pid}&status=active&limit=3`),
        ]);
        // Show today's ritual the moment it lands (it's created just before the ideas)
        if (r.data.some((x) => x.dailyFor === today)) setRituals(r.data);
        if (en.data[0]) {
          setEnrichment(en.data[0]);
          setRituals(r.data);
          toast.success("Today's ritual and enrichment ideas are ready.");
          return;
        }
      } catch (e) { console.debug(`Daily plan poll ${attempt + 1} failed, retrying:`, e?.message); }
    }
    toast.info("Today's plan is taking a little longer than usual — it will appear here shortly. Pull to refresh in a minute.");
  };

  const submitCheckin = async () => {
    if (!ci.humanMood || !ci.petMood) { toast.error("Please describe both moods."); return; }
    setCiBusy(true);
    try {
      const { data: created } = await api.post("/daily-checkins", { ...ci, petId, date: new Date().toISOString().slice(0, 10) });
      toast.success(created.autoBondScore
        ? `Your first Bond Score is ready: ${created.autoBondScore.score}/100. Crafting today's ritual...`
        : "Check-in saved. Crafting today's ritual and enrichment ideas...");
      setCi({ humanMood: "", petMood: "", stressLevel: 5, connectionQuality: 7, notes: "" });
      // Streak celebration: refresh and cheer on milestones (3, 7, 14, 30, 50, 100, 365)
      const prevStreak = streak?.current ?? 0;
      const s = await fetchStreak();
      if (s?.todayDone && s.current > prevStreak) {
        if (s.isMilestone) {
          toast.success(`${s.current}-day check-in streak! ${pet?.name || "Your pet"} is lucky to have you.`, { duration: 6000 });
        } else if (s.current > 1) {
          toast.info(`Streak extended — ${s.current} days in a row.`);
        }
      }
      const { data } = await api.post("/bond-scores/calculate", { petId });
      setScore(data);
      const c = await api.get(`/daily-checkins?petId=${petId}&limit=5`);
      setCheckins(c.data);
      refreshDailyPlan(petId); // fire-and-forget polling
    } catch (e) { toast.error(errMsg(e)); } finally { setCiBusy(false); }
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground" data-testid="dashboard-loading">Loading your dashboard...</div>;

  if (!pets.length) {
    return (
      <div className="max-w-2xl mx-auto py-8">
        <OnboardingChecklist userId={user?.id} role={user?.role} petsCount={0} hasCheckin={false} hasScore={false} />
        <EmptyState
          icon={PawPrint}
          title="Let's meet your animal"
          hint="Add your first pet profile to unlock check-ins, rituals, the Bond Score, and the AI coach."
          action={<Link to="/pets"><Button className="rounded-xl" data-testid="dashboard-add-pet-button">Add my pet</Button></Link>}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Good to see you, {user?.name?.split(" ")[0]}.</h1>
            <HelpTip title={PAGE_HELP.dashboard.title} text={PAGE_HELP.dashboard.text} testId="page-help-dashboard" />
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">One small step today keeps the bond growing.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StreakChip streak={streak} />
          <PetSelect pets={pets} value={petId} onChange={setPetId} testId="dashboard-pet-select" />
        </div>
      </div>

      <OnboardingChecklist userId={user?.id} role={user?.role} petsCount={pets.length} hasCheckin={checkins.length > 0} hasScore={!!score} />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        {/* Quick Daily Check-In - the daily habit, so it lives at the top */}
        <QuickCheckinCard pet={pet} ci={ci} setCi={setCi} busy={ciBusy} onSubmit={submitCheckin} streak={streak} />
        <BondScoreCard pet={pet} score={score} calcBusy={calcBusy} onCalc={calcScore} />
        {/* Today's Enrichment Ideas (auto-generated with the daily ritual after check-in) */}
        <EnrichmentCard pet={pet} enrichment={enrichment} onRegenerate={regeneratePlan} regenBusy={regenBusy} />
        <RitualCard ritual={rituals[0]} onRegenerate={regeneratePlan} regenBusy={regenBusy} />
        <WellnessSnapshotCard latestWellness={wellness[0]} />
        <MoodSnapshotCard pet={pet} latestCheckin={checkins[0]} />
        <ReportsReadyCard reports={reports} />
        <CoachPromptCard pet={pet} coachPrompt={coachPrompt} setCoachPrompt={setCoachPrompt} onAsk={() => navigate("/coach", { state: { prompt: coachPrompt, petId } })} />
        <CareCircleCard careCircle={careCircle} />
        <RecentLogsCard wellness={wellness} />
      </div>
    </div>
  );
}
